/**
 * Organization Store (Zustand) - UNIFIED
 *
 * @deprecated This store mixes data cache with UI state.
 * Migrate to the new architecture:
 *
 * **Data fetching/mutations** → Use TanStack Query hooks:
 * ```tsx
 * import { useOrganizations, useCreateOrganization } from '@features/organizations/queries';
 * ```
 *
 * **UI-only state** → Use the new UI store:
 * ```tsx
 * import { useOrganizationUIStore } from '@features/organizations/store/organizationUIStore';
 * ```
 *
 * This store will be removed in a future version.
 * See: apps/web/docs/ARCHITECTURE_MIGRATION_PLAN.md
 *
 * ---
 * Legacy: Global state management for organization data with:
 * - CRUD operations (create, update, delete)
 * - Selection state management
 * - Comprehensive filtering
 * - Owner management
 *
 * All data is read from FastAPI cache (no direct Pipedrive pagination).
 */

import { create } from 'zustand';
import {
  fetchOrganizations,
  fetchOrganizationById,
  fetchOwners,
  fetchCampaignOrganizations,
  createOrganization as createOrgAPI,
  updateOrganization as updateOrgAPI,
  deleteOrganization as deleteOrgAPI,
  savePendingOrganizations,
  fetchPendingOrganizations,
} from '../api/organizationApi';
import { useCampaignStore } from '../../campaigns/store/campaignStore';
import type {
  OrganizationItem,
  OrganizationListParams,
  OrganizationCreateRequest,
  OrganizationUpdateRequest,
} from '@shared/types/api';

/**
 * Owner for filter dropdown
 */
export interface Owner {
  id: number;
  name: string;
  email: string;
}

/**
 * Organization store state and actions
 */
interface OrganizationStore {
  // State
  organizations: OrganizationItem[];
  campaignOrganizations: OrganizationItem[];
  isLoading: boolean;
  isLoadingOwners: boolean;
  error: string | null;
  owners: Owner[];

  // Selection state
  selectedPreviewIds: Set<number>;
  selectedFinalIds: number[];
  selectedRemovalIds: Set<number>;
  originalSelectedIds: number[]; // Original IDs when campaign was loaded (for diff calculation)

  // Read Actions
  fetchOrganizations: (params?: OrganizationListParams) => Promise<void>;
  fetchCampaignOrganizations: (campaignId: string) => Promise<void>;
  fetchById: (id: number) => Promise<OrganizationItem | null>;
  fetchOwners: () => Promise<void>;
  searchOrganizations: (query: string) => OrganizationItem[];
  filterByCity: (city: string) => OrganizationItem[];
  filterByOwner: (ownerId: number) => OrganizationItem[];
  filterByQuadrant: (quadrant: string) => OrganizationItem[];
  hasAddress: () => OrganizationItem[];

  // CRUD Actions
  createOrganization: (data: OrganizationCreateRequest) => Promise<OrganizationItem>;
  updateOrganization: (id: number, data: OrganizationUpdateRequest) => Promise<OrganizationItem>;
  deleteOrganization: (id: number) => Promise<void>;

  // Utility Actions
  clearError: () => void;
  reset: () => void;

  // Selection actions
  togglePreviewSelection: (id: number) => void;
  selectAllPreview: (visibleIds: number[]) => void;
  deselectAllPreview: () => void;
  addToFinal: (ids?: number[]) => void;
  removeFromFinal: (ids?: number[]) => void;
  clearFinal: () => void;
  clearSelections: () => void;
  toggleRemovalSelection: (id: number) => void;
  selectAllRemoval: () => void;
  deselectAllRemoval: () => void;
  resetSelections: () => void;
  swapLists: () => void;
  saveToCampaign: (campaignId: string) => Promise<void>;
  setSelectedFinalIds: (ids: number[]) => void;
  setOriginalSelectedIds: (ids: number[]) => void;

  // Pending selection persistence (Phase 2)
  savePendingSelections: (campaignId: string) => Promise<void>;
  fetchPendingSelections: (campaignId: string) => Promise<void>;
  scheduleAutoSave: (campaignId: string) => void;
  cancelAutoSave: () => void;

  // Route action sync (Phase 3)
  updateOrganizationStatus: (orgId: number, status: 'mailer' | 'removed' | 'deleted') => void;
}

/**
 * Initial state
 */
const initialState = {
  organizations: [],
  campaignOrganizations: [],
  isLoading: false,
  isLoadingOwners: false,
  error: null,
  owners: [],
  selectedPreviewIds: new Set<number>(),
  selectedFinalIds: [],
  selectedRemovalIds: new Set<number>(),
  originalSelectedIds: [] as number[], // Original IDs when campaign was loaded
};

/**
 * Create organization store
 *
 * Phase 2: Uses backend API persistence instead of sessionStorage.
 * Selections are auto-saved to backend with 2-second debounce.
 */
export const useOrganizationStore = create<OrganizationStore>((set, get) => ({
  ...initialState,

  /**
   * Fetch all organizations from cache
   *
   * No pagination - reads entire cache from FastAPI.
   *
   * @param params - Optional filter parameters (filter_15dp, has_address, etc.)
   * @example
   * ```tsx
   * const { fetchOrganizations } = useOrganizationStore();
   * await fetchOrganizations({ filter_15dp: true });
   * ```
   */
  fetchOrganizations: async (params?: OrganizationListParams) => {
    set({ isLoading: true, error: null });

    try {
      // Fetch with optional filter params, request maximum allowed results
      const result = await fetchOrganizations({
        ...params,
        per_page: 100, // API maximum is 100
      });

      set({
        organizations: result.organizations,
        isLoading: false,
      });
    } catch (error) {
      console.error('[OrganizationStore] Failed to fetch organizations:', error);
      set({
        error: (error as Error).message || 'Failed to fetch organizations',
        isLoading: false,
      });
    }
  },

  /**
   * Fetch organizations for a specific campaign
   *
   * Bypasses filter state and loads ALL organizations associated with the campaign.
   * This ensures DMS parity where all campaign organizations appear in
   * "Organizations Selected" table regardless of current filter settings.
   *
   * @param campaignId - Campaign ID (e.g., "CAMPAIGN_20251028_121851")
   *
   * @example
   * ```tsx
   * const { fetchCampaignOrganizations } = useOrganizationStore();
   * await fetchCampaignOrganizations('CAMPAIGN_20251028_121851');
   * // Now campaignOrganizations contains all 67 organizations
   * ```
   */
  fetchCampaignOrganizations: async (campaignId: string) => {
    set({ isLoading: true, error: null });

    try {
      const orgs = await fetchCampaignOrganizations(campaignId);
      const orgIds = orgs.map((org) => org.id);
      set({
        campaignOrganizations: orgs,
        selectedFinalIds: orgIds,
        originalSelectedIds: orgIds, // Store original for diff calculation on save
        isLoading: false,
      });
    } catch (error) {
      console.error(`[OrganizationStore] Failed to fetch campaign organizations:`, error);
      set({
        error: (error as Error).message || 'Failed to fetch campaign organizations',
        isLoading: false,
      });
    }
  },

  /**
   * Fetch single organization by ID
   *
   * @param id - Organization ID
   * @returns Promise resolving to organization or null
   *
   * @example
   * ```tsx
   * const org = await fetchById(123);
   * ```
   */
  fetchById: async (id: number): Promise<OrganizationItem | null> => {
    try {
      const org = await fetchOrganizationById(id);
      return org;
    } catch (error) {
      console.error(`[OrganizationStore] Failed to fetch org ${id}:`, error);
      return null;
    }
  },

  /**
   * Fetch unique Pipedrive owners for filter dropdown
   *
   * @example
   * ```tsx
   * const { fetchOwners } = useOrganizationStore();
   * await fetchOwners();
   * ```
   */
  fetchOwners: async () => {
    set({ isLoadingOwners: true, error: null });

    try {
      const owners = await fetchOwners();
      set({
        owners,
        isLoadingOwners: false,
      });
    } catch (error) {
      console.error('[OrganizationStore] Failed to fetch owners:', error);
      set({
        error: (error as Error).message || 'Failed to fetch owners',
        isLoadingOwners: false,
      });
    }
  },

  /**
   * Search organizations by name (client-side)
   *
   * @param query - Search query (case-insensitive)
   * @returns Filtered organizations
   *
   * @example
   * ```tsx
   * const results = searchOrganizations('ACME');
   * ```
   */
  searchOrganizations: (query: string): OrganizationItem[] => {
    const { organizations } = get();
    const lowerQuery = query.toLowerCase();

    return organizations.filter(org =>
      org.name?.toLowerCase().includes(lowerQuery) ||
      org.address?.toLowerCase().includes(lowerQuery)
    );
  },

  /**
   * Filter organizations by city (client-side)
   *
   * @param city - City name (case-insensitive)
   * @returns Filtered organizations
   *
   * @example
   * ```tsx
   * const houstonOrgs = filterByCity('Houston');
   * ```
   */
  filterByCity: (city: string): OrganizationItem[] => {
    const { organizations } = get();
    const lowerCity = city.toLowerCase();

    return organizations.filter(org =>
      org.city?.toLowerCase().includes(lowerCity)
    );
  },

  /**
   * Filter organizations by owner (client-side)
   *
   * @param ownerId - Owner ID
   * @returns Filtered organizations
   *
   * @example
   * ```tsx
   * const myOrgs = filterByOwner(16122048);
   * ```
   */
  filterByOwner: (ownerId: number): OrganizationItem[] => {
    const { organizations } = get();

    return organizations.filter(org => org.owner?.id === ownerId);
  },

  /**
   * Filter organizations by quadrant (client-side)
   *
   * @param quadrant - Quadrant name
   * @returns Filtered organizations
   *
   * @example
   * ```tsx
   * const quadrantOrgs = filterByQuadrant('NW');
   * ```
   */
  filterByQuadrant: (quadrant: string): OrganizationItem[] => {
    const { organizations } = get();

    return organizations.filter(org => org.quadrant != null && org.quadrant === quadrant);
  },

  /**
   * Filter organizations that have addresses (client-side)
   *
   * @returns Organizations with non-null addresses
   *
   * @example
   * ```tsx
   * const orgsWithAddress = hasAddress();
   * ```
   */
  hasAddress: (): OrganizationItem[] => {
    const { organizations } = get();

    return organizations.filter(org => org.address != null && org.address.trim() !== '');
  },

  /**
   * Create new organization with optimistic UI update
   *
   * @param data - Organization creation data
   * @returns Promise resolving to created organization
   *
   * @example
   * ```tsx
   * const newOrg = await createOrganization({
   *   name: 'ACME Corp',
   *   address: '123 Main St',
   *   owner_id: 16122048,
   * });
   * ```
   */
  createOrganization: async (data: OrganizationCreateRequest): Promise<OrganizationItem> => {
    const tempId = -Date.now(); // Temporary negative ID
    const tempOrg: OrganizationItem = {
      id: tempId,
      name: data.name,
      address: data.address || null,
      city: null,
      state: null,
      postal_code: null,
      quadrant: null,
      next_activity_date: null,
      next_activity_type: null,
      owner: {
        id: data.owner_id || 0,
        name: 'Unknown',
        email: '',
      },
      contacts_count: 0,
      activities_count: 0,
      open_deals_count: 0,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    // Optimistic update
    set((state) => ({
      organizations: [tempOrg, ...state.organizations],
      isLoading: true,
      error: null,
    }));

    try {
      const newOrg = await createOrgAPI(data);

      // Replace temp with real
      set((state) => ({
        organizations: state.organizations.map((org) =>
          org.id === tempId ? newOrg : org
        ),
        isLoading: false,
      }));

      return newOrg;
    } catch (error) {
      // Rollback on error
      set((state) => ({
        organizations: state.organizations.filter((org) => org.id !== tempId),
        error: (error as Error).message || 'Failed to create organization',
        isLoading: false,
      }));
      throw error;
    }
  },

  /**
   * Update organization with optimistic UI update
   *
   * @param id - Organization ID
   * @param data - Organization update data
   * @returns Promise resolving to updated organization
   *
   * @example
   * ```tsx
   * const updatedOrg = await updateOrganization(123, {
   *   name: 'Updated Name',
   * });
   * ```
   */
  updateOrganization: async (
    id: number,
    data: OrganizationUpdateRequest
  ): Promise<OrganizationItem> => {
    // Store original for rollback
    const { organizations } = get();
    const original = organizations.find((org) => org.id === id);

    if (!original) {
      throw new Error(`Organization ${id} not found in store`);
    }

    // Optimistic update
    set((state) => ({
      organizations: state.organizations.map((org) =>
        org.id === id ? { ...org, ...data, updated_at: new Date().toISOString() } : org
      ),
      isLoading: true,
      error: null,
    }));

    try {
      const updatedOrg = await updateOrgAPI(id, data);

      // Replace with API response
      set((state) => ({
        organizations: state.organizations.map((org) =>
          org.id === id ? updatedOrg : org
        ),
        isLoading: false,
      }));

      return updatedOrg;
    } catch (error) {
      // Rollback on error
      set((state) => ({
        organizations: state.organizations.map((org) =>
          org.id === id ? original : org
        ),
        error: (error as Error).message || 'Failed to update organization',
        isLoading: false,
      }));
      throw error;
    }
  },

  /**
   * Delete organization with optimistic UI update
   *
   * @param id - Organization ID
   * @returns Promise resolving when deletion completes
   *
   * @example
   * ```tsx
   * await deleteOrganization(123);
   * ```
   */
  deleteOrganization: async (id: number): Promise<void> => {
    // Store original for rollback
    const { organizations } = get();
    const original = organizations.find((org) => org.id === id);

    if (!original) {
      throw new Error(`Organization ${id} not found in store`);
    }

    // Optimistic delete
    set((state) => ({
      organizations: state.organizations.filter((org) => org.id !== id),
      isLoading: true,
      error: null,
    }));

    try {
      await deleteOrgAPI(id);

      set({ isLoading: false });
    } catch (error) {
      // Rollback on error
      set((state) => ({
        organizations: [...state.organizations, original].sort((a, b) => a.id - b.id),
        error: (error as Error).message || 'Failed to delete organization',
        isLoading: false,
      }));
      throw error;
    }
  },

  /**
   * Clear error state
   */
  clearError: () => {
    set({ error: null });
  },

  /**
   * Reset store to initial state
   */
  reset: () => {
    set(initialState);
  },

  /**
   * Toggle individual preview selection
   */
  togglePreviewSelection: (id: number) => {
    set((state) => {
      const newSelection = new Set(state.selectedPreviewIds);
      if (newSelection.has(id)) {
        newSelection.delete(id);
      } else {
        newSelection.add(id);
      }
      return { selectedPreviewIds: newSelection };
    });

    // Auto-save pending selections
    const selectedCampaignId = useCampaignStore.getState().selectedCampaignId;
    if (selectedCampaignId) {
      get().scheduleAutoSave(selectedCampaignId);
    }
  },

  /**
   * Select all visible preview organizations
   */
  selectAllPreview: (visibleIds: number[]) => {
    set({ selectedPreviewIds: new Set(visibleIds) });

    // Auto-save pending selections
    const selectedCampaignId = useCampaignStore.getState().selectedCampaignId;
    if (selectedCampaignId) {
      get().scheduleAutoSave(selectedCampaignId);
    }
  },

  /**
   * Deselect all preview organizations
   */
  deselectAllPreview: () => {
    set({ selectedPreviewIds: new Set() });
  },

  /**
   * Add organizations to final list
   * @param ids - Optional array of IDs to add. If not provided, uses selectedPreviewIds.
   */
  addToFinal: (ids?: number[]) => {
    set((state) => {
      const newFinalIds = [...state.selectedFinalIds];
      // Use provided IDs if available, otherwise use selectedPreviewIds
      const idsToAdd = ids && ids.length > 0 ? ids : Array.from(state.selectedPreviewIds);
      let addedCount = 0;
      idsToAdd.forEach((id) => {
        if (!newFinalIds.includes(id)) {
          newFinalIds.push(id);
          addedCount++;
        }
      });
      console.log(`[OrganizationStore] Added ${addedCount} orgs to final selections. Total: ${newFinalIds.length}`);
      return {
        selectedFinalIds: newFinalIds,
        selectedPreviewIds: ids ? state.selectedPreviewIds : new Set(), // Only clear preview if using preview selection
      };
    });

    // Auto-save pending selections
    const selectedCampaignId = useCampaignStore.getState().selectedCampaignId;
    if (selectedCampaignId) {
      get().scheduleAutoSave(selectedCampaignId);
    }
  },

  /**
   * Remove organizations from final list
   * @param ids - Optional array of IDs to remove. If not provided, uses selectedRemovalIds.
   */
  removeFromFinal: (ids?: number[]) => {
    set((state) => {
      // Use provided IDs if available, otherwise use selectedRemovalIds
      const idsToRemove = ids && ids.length > 0 ? new Set(ids) : state.selectedRemovalIds;
      const newFinalIds = state.selectedFinalIds.filter(
        (id) => !idsToRemove.has(id)
      );
      return {
        selectedFinalIds: newFinalIds,
        selectedRemovalIds: ids ? state.selectedRemovalIds : new Set(), // Only clear removal if using removal selection
      };
    });

    // Auto-save pending selections
    const selectedCampaignId = useCampaignStore.getState().selectedCampaignId;
    if (selectedCampaignId) {
      get().scheduleAutoSave(selectedCampaignId);
    }
  },

  /**
   * Clear all final selections
   */
  clearFinal: () => {
    const { selectedFinalIds } = get();
    console.log(`[OrganizationStore] Clearing ${selectedFinalIds.length} final selections`);
    set({ selectedFinalIds: [], selectedRemovalIds: new Set() });
  },

  /**
   * Clear all selections (preview + final)
   */
  clearSelections: () => {
    set({
      selectedPreviewIds: new Set(),
      selectedFinalIds: [],
      selectedRemovalIds: new Set(),
    });
  },

  /**
   * Toggle removal selection (checkboxes in selected table)
   */
  toggleRemovalSelection: (id: number) => {
    set((state) => {
      const newSelection = new Set(state.selectedRemovalIds);
      if (newSelection.has(id)) {
        newSelection.delete(id);
      } else {
        newSelection.add(id);
      }
      return { selectedRemovalIds: newSelection };
    });
  },

  /**
   * Select all organizations in final list for removal
   */
  selectAllRemoval: () => {
    set((state) => ({
      selectedRemovalIds: new Set(state.selectedFinalIds),
    }));
  },

  /**
   * Deselect all removal selections
   */
  deselectAllRemoval: () => {
    set({ selectedRemovalIds: new Set() });
  },

  /**
   * Reset all selection state
   */
  resetSelections: () => {
    set({
      selectedPreviewIds: new Set(),
      selectedFinalIds: [],
      selectedRemovalIds: new Set(),
    });
  },

  /**
   * Swap organization lists between preview and selected
   *
   * Swaps ALL organizations between Preview and Selected tables.
   * Preview (all non-selected orgs) → Selected
   * Selected (all selected orgs) → Preview
   */
  swapLists: () => {
    set((state) => {
      // Get ALL organization IDs
      const allOrgIds = state.organizations.map(org => org.id);

      // Current selected IDs
      const currentSelectedIds = [...state.selectedFinalIds];

      // Preview IDs = all orgs that are NOT in selected
      const currentPreviewIds = allOrgIds.filter(id => !currentSelectedIds.includes(id));

      console.log(`[OrganizationStore] Swapping lists:`, {
        previewCount: currentPreviewIds.length,
        selectedCount: currentSelectedIds.length,
        totalOrgs: allOrgIds.length,
      });

      // Swap: Preview → Selected, Selected → Preview
      const newSelectedIds = currentPreviewIds;

      console.log(`[OrganizationStore] Swap complete:`, {
        newPreviewCount: currentSelectedIds.length,
        newSelectedCount: newSelectedIds.length,
      });

      return {
        selectedFinalIds: newSelectedIds,
        selectedPreviewIds: new Set(), // Clear preview checkboxes
        selectedRemovalIds: new Set(), // Clear removal checkboxes
      };
    });
  },

  /**
   * Save selected organizations to a campaign
   *
   * Calls POST /campaigns/{id}/organizations API endpoint.
   * Clears selections on success.
   *
   * @param campaignId - Campaign ID to save organizations to
   * @returns Promise resolving when save completes
   *
   * @example
   * ```tsx
   * const { saveToCampaign } = useOrganizationStore();
   * await saveToCampaign('CAMPAIGN_20251028_121851');
   * // Organizations saved, selections cleared
   * ```
   */
  saveToCampaign: async (campaignId: string): Promise<void> => {
    const { selectedFinalIds } = get();

    if (selectedFinalIds.length === 0) {
      throw new Error('No organizations selected');
    }

    set({ isLoading: true, error: null });

    try {
      const { addOrganizationsToCampaign } = await import('../../campaigns/api/campaignApi');
      await addOrganizationsToCampaign(campaignId, {
        organization_ids: selectedFinalIds,
      });

      // Clear selections on success
      set({
        selectedFinalIds: [],
        selectedRemovalIds: new Set(),
        selectedPreviewIds: new Set(),
        isLoading: false,
      });
    } catch (error) {
      console.error(`[OrganizationStore] Failed to save to campaign ${campaignId}:`, error);
      set({
        error: (error as Error).message || 'Failed to save organizations to campaign',
        isLoading: false,
      });
      throw error;
    }
  },

  /**
   * Set selected final IDs directly
   *
   * Used to sync campaign organizations to the selectedFinalIds when a campaign is loaded.
   * This ensures the "Selected Organizations" table shows the campaign's organizations.
   *
   * @param ids - Array of organization IDs to set as selected
   *
   * @example
   * ```tsx
   * const { setSelectedFinalIds } = useOrganizationStore();
   * setSelectedFinalIds([123, 456, 789]);
   * ```
   */
  setSelectedFinalIds: (ids: number[]): void => {
    console.log(`[OrganizationStore] Setting selectedFinalIds to ${ids.length} organizations`);
    set({
      selectedFinalIds: ids,
      selectedRemovalIds: new Set(), // Clear removal checkboxes when new orgs are loaded
    });
  },

  /**
   * Set original selected IDs (snapshot when campaign is loaded)
   *
   * Used to track original selection for diff calculation when saving.
   * This allows saveToCampaign to determine which orgs were added vs removed.
   *
   * @param ids - Array of organization IDs that were originally selected
   *
   * @example
   * ```tsx
   * const { setOriginalSelectedIds } = useOrganizationStore();
   * setOriginalSelectedIds([123, 456, 789]);
   * ```
   */
  setOriginalSelectedIds: (ids: number[]): void => {
    console.log(`[OrganizationStore] Setting originalSelectedIds to ${ids.length} organizations`);
    set({ originalSelectedIds: ids });
  },

  /**
   * Save pending selections to backend (Phase 2)
   *
   * Persists current selection state (preview, final, removal) to backend.
   * Auto-called by scheduleAutoSave with 2-second debounce.
   *
   * @param campaignId - Campaign ID to save selections for
   * @returns Promise resolving when save completes
   *
   * @example
   * ```tsx
   * await savePendingSelections('CAMPAIGN_20251028_121851');
   * ```
   */
  savePendingSelections: async (campaignId: string): Promise<void> => {
    const { selectedPreviewIds, selectedFinalIds, selectedRemovalIds } = get();

    try {
      await savePendingOrganizations(campaignId, {
        preview_ids: Array.from(selectedPreviewIds),
        final_ids: selectedFinalIds,
        removal_ids: Array.from(selectedRemovalIds),
      });
      console.log('[OrganizationStore] Saved pending selections to backend');
    } catch (error) {
      console.error('[OrganizationStore] Failed to save pending selections:', error);
      throw error;
    }
  },

  /**
   * Fetch pending selections from backend (Phase 2)
   *
   * Loads saved selection state (preview, final, removal) from backend.
   * Called on campaign change to restore user selections.
   *
   * @param campaignId - Campaign ID to load selections for
   * @returns Promise resolving when load completes
   *
   * @example
   * ```tsx
   * await fetchPendingSelections('CAMPAIGN_20251028_121851');
   * ```
   */
  fetchPendingSelections: async (campaignId: string): Promise<void> => {
    try {
      const data = await fetchPendingOrganizations(campaignId);

      // Double-check data exists and has expected structure
      if (!data) {
        console.warn('[OrganizationStore] No pending selections data returned, using empty defaults');
        set({
          selectedPreviewIds: new Set(),
          selectedFinalIds: [],
          selectedRemovalIds: new Set(),
        });
        return;
      }

      set({
        selectedPreviewIds: new Set(data.preview_ids || []),
        selectedFinalIds: data.final_ids || [],
        selectedRemovalIds: new Set(data.removal_ids || []),
      });
      console.log('[OrganizationStore] Loaded pending selections from backend', {
        previewCount: data.preview_ids?.length || 0,
        finalCount: data.final_ids?.length || 0,
        removalCount: data.removal_ids?.length || 0,
      });
    } catch (error) {
      console.error('[OrganizationStore] Failed to load pending selections:', error);
      // Set empty defaults on error to prevent undefined access
      set({
        selectedPreviewIds: new Set(),
        selectedFinalIds: [],
        selectedRemovalIds: new Set(),
      });
      // Don't throw - allow UI to continue with empty selections
    }
  },

  /**
   * Schedule auto-save with 2-second debounce (Phase 2)
   *
   * Debounced auto-save to prevent excessive backend calls.
   * Cancels previous timer and schedules new save.
   *
   * NOTE: autoSaveTimer is stored outside Zustand state because:
   * - It's an internal implementation detail (not part of public state)
   * - NodeJS.Timeout is not serializable (breaks devtools/persistence)
   * - Timer cleanup happens in cancelAutoSave() before component unmount
   *
   * @param campaignId - Campaign ID to save selections for
   *
   * @example
   * ```tsx
   * scheduleAutoSave('CAMPAIGN_20251028_121851');
   * ```
   */
  scheduleAutoSave: (campaignId: string): void => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const storeState = get() as any;
    const autoSaveTimer: NodeJS.Timeout | undefined = storeState.autoSaveTimer;

    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
    }

    const newTimer = setTimeout(async () => {
      try {
        await get().savePendingSelections(campaignId);
      } catch (error) {
        console.error('[OrganizationStore] Auto-save failed:', error);
      }
    }, 2000); // 2-second debounce

    // Store timer reference for cleanup
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (set as any)({ autoSaveTimer: newTimer });
  },

  /**
   * Cancel pending auto-save (Phase 2)
   *
   * Clears scheduled auto-save timer.
   *
   * @example
   * ```tsx
   * cancelAutoSave();
   * ```
   */
  cancelAutoSave: (): void => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const storeState = get() as any;
    const autoSaveTimer: NodeJS.Timeout | undefined = storeState.autoSaveTimer;

    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (set as any)({ autoSaveTimer: null });
    }
  },

  /**
   * Update organization status for route action sync (Phase 3)
   *
   * Updates local state to reflect route action events:
   * - 'mailer': Organization moved to mailers list
   * - 'removed': Organization removed from route
   * - 'deleted': Organization deleted from campaign
   *
   * Note: This is a local-only update for UI synchronization.
   * The backend has already performed the actual action.
   *
   * @param orgId - Organization ID
   * @param status - New status
   *
   * @example
   * ```tsx
   * // Called by useRouteActionSync when SSE event received
   * updateOrganizationStatus(123, 'removed');
   * ```
   */
  updateOrganizationStatus: (orgId: number, status: 'mailer' | 'removed' | 'deleted'): void => {
    console.log(`[OrganizationStore] Updating org ${orgId} status to '${status}'`);

    set((state) => {
      // For 'deleted' status, remove org from all lists
      if (status === 'deleted') {
        return {
          organizations: state.organizations.filter((org) => org.id !== orgId),
          campaignOrganizations: state.campaignOrganizations.filter((org) => org.id !== orgId),
          selectedFinalIds: state.selectedFinalIds.filter((id) => id !== orgId),
          selectedPreviewIds: new Set(
            Array.from(state.selectedPreviewIds).filter((id) => id !== orgId)
          ),
          selectedRemovalIds: new Set(
            Array.from(state.selectedRemovalIds).filter((id) => id !== orgId)
          ),
        };
      }

      // For 'mailer' or 'removed', keep org in list but update metadata
      // Note: We don't have a status field in OrganizationItem currently,
      // so we just log the change. The UI can infer status from route data.
      // Future: Add 'route_status' field to OrganizationItem if needed.
      return state;
    });
  },
}));
